import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { dateTime, money, shortDate, type Bonspiel, type Draw, type League } from '@lcc/shared';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useCurrentSeason } from '../lib/hooks';
import { Card, Empty, ErrorNote, Loading, Stat, Status } from '../components/ui';
import { PageHeader } from '../App';

interface MoneySummary {
  summary: {
    invoices: number; billed_cents: number; collected_cents: number;
    outstanding_cents: number; open_invoices: number; overdue: number;
  };
}
interface Outstanding { count: number; data: Array<{ memberId: string; fullName: string; outstandingCount: number }> }

export function DashboardPage() {
  const { user, can, isAdmin } = useAuth();
  const season = useCurrentSeason();

  const leagues = useQuery({
    queryKey: ['leagues'],
    queryFn: () => api.get<{ data: League[] }>('/leagues').then((r) => r.data),
  });

  const upcoming = useQuery({
    queryKey: ['draws', 'upcoming'],
    queryFn: () => {
      const from = new Date().toISOString().slice(0, 10);
      const to = new Date(Date.now() + 14 * 864e5).toISOString().slice(0, 10);
      return api.get<{ data: Draw[] }>(`/draws?from=${from}&to=${to}`).then((r) => r.data);
    },
  });

  const spiels = useQuery({
    queryKey: ['bonspiels'],
    queryFn: () => api.get<{ data: Bonspiel[] }>('/bonspiels').then((r) => r.data),
  });

  const waivers = useQuery({
    queryKey: ['waivers', 'outstanding'],
    queryFn: () => api.get<Outstanding>('/waivers/outstanding'),
    enabled: isAdmin,
  });

  const treasury = useQuery({
    queryKey: ['money', 'summary', season.data?.id],
    queryFn: () => api.get<MoneySummary>(`/money/summary?seasonId=${season.data!.id}`),
    enabled: isAdmin && Boolean(season.data?.id),
  });

  const memberCount = useQuery({
    queryKey: ['members', 'count'],
    queryFn: () => api.get<{ total: number }>('/members?perPage=1').then((r) => r.total),
  });

  const nextSpiel = spiels.data
    ?.filter((s) => s.startsOn >= new Date().toISOString().slice(0, 10))
    .sort((a, b) => a.startsOn.localeCompare(b.startsOn))[0];

  const unscored = upcoming.data
    ?.flatMap((d) => d.games.map((g) => ({ draw: d, game: g })))
    .filter(({ draw, game }) =>
      new Date(draw.scheduledAt) < new Date() && game.status === 'scheduled');

  return (
    <>
      <PageHeader
        title={`Good curling, ${user?.fullName?.split(' ')[0] ?? ''}`}
        sub={season.data
          ? `Season ${season.data.name} · ${shortDate(season.data.startsOn)} to ${shortDate(season.data.endsOn)}`
          : 'No current season set'}
      />

      <div className="content stack">
        <div className="grid grid--4">
          <Stat label="Members" value={memberCount.data ?? '—'} note="active on the roster" />
          <Stat label="Leagues" value={leagues.data?.length ?? '—'} note="this season" />
          <Stat
            label="Waivers outstanding"
            value={waivers.data?.count ?? (isAdmin ? '—' : 'n/a')}
            note={waivers.data?.count ? 'members cannot be rostered' : 'everyone is cleared'}
            alert={Boolean(waivers.data?.count)}
          />
          <Stat
            label="Fees outstanding"
            value={treasury.data ? money(treasury.data.summary.outstanding_cents) : (isAdmin ? '—' : 'n/a')}
            note={treasury.data ? `${treasury.data.summary.open_invoices} open invoices` : undefined}
            alert={Boolean(treasury.data?.summary.overdue)}
          />
        </div>

        {unscored && unscored.length > 0 && (
          <div className="banner banner--warn">
            <strong>{unscored.length} game{unscored.length === 1 ? '' : 's'} still need a score.</strong>{' '}
            Standings will not be right until they are entered.{' '}
            <Link to="/schedule">Enter scores &rarr;</Link>
          </div>
        )}

        <div className="grid grid--2">
          <Card title="Next two weeks" actions={<Link className="btn btn--sm" to="/schedule">All draws</Link>}>
            {upcoming.isLoading ? <Loading /> : upcoming.error ? <ErrorNote error={upcoming.error} /> : (
              upcoming.data && upcoming.data.length > 0 ? (
                <div className="stack stack--tight">
                  {upcoming.data.slice(0, 8).map((d) => {
                    const league = leagues.data?.find((l) => l.id === d.leagueId);
                    return (
                      <div key={d.id} className="spread small"
                        style={{ paddingBottom: '.45rem', borderBottom: '1px solid var(--border)' }}>
                        <span>
                          <b>{league?.name ?? 'League'}</b> · draw {d.drawNumber}
                          <br /><span className="muted">{dateTime(d.scheduledAt)}</span>
                        </span>
                        <span className="row row--tight">
                          <span className="muted">{d.games.length} game{d.games.length === 1 ? '' : 's'}</span>
                          <Status value={d.status} />
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : <Empty title="Nothing scheduled">Generate a schedule from a league page.</Empty>
            )}
          </Card>

          <div className="stack">
            <Card title="Next bonspiel" actions={<Link className="btn btn--sm" to="/bonspiels">All spiels</Link>}>
              {nextSpiel ? (
                <div className="stack stack--tight">
                  <h3 style={{ margin: 0 }}>
                    <Link to={`/bonspiels/${nextSpiel.id}`}>{nextSpiel.name}</Link>
                  </h3>
                  <div className="row row--tight small">
                    <Status value={nextSpiel.kind === 'open' ? 'open' : 'in_house'} />
                    <span className="muted">{shortDate(nextSpiel.startsOn)}</span>
                    {nextSpiel.maxTeams && (
                      <span className="muted">
                        {nextSpiel.entryCount ?? 0} of {nextSpiel.maxTeams} entered
                      </span>
                    )}
                  </div>
                  {nextSpiel.sponsorText && <p className="small muted">{nextSpiel.sponsorText}</p>}
                </div>
              ) : <Empty title="No upcoming spiels" />}
            </Card>

            {can() && waivers.data && waivers.data.count > 0 && (
              <Card title="Waivers to chase"
                actions={<Link className="btn btn--sm" to="/waivers">Open</Link>}>
                <div className="stack stack--tight small">
                  {waivers.data.data.slice(0, 6).map((m) => (
                    <div key={m.memberId} className="spread">
                      <Link to={`/members/${m.memberId}`}>{m.fullName}</Link>
                      <span className="pill pill--warn">{m.outstandingCount} outstanding</span>
                    </div>
                  ))}
                  {waivers.data.count > 6 && (
                    <p className="muted">and {waivers.data.count - 6} more</p>
                  )}
                </div>
              </Card>
            )}
          </div>
        </div>

        <Card title="Leagues" flush actions={<Link className="btn btn--sm" to="/leagues">Manage</Link>}>
          {leagues.isLoading ? <Loading /> : (
            <div className="table-wrap">
              <table className="t">
                <thead>
                  <tr>
                    <th>League</th><th>Format</th><th className="num">Teams</th>
                    <th className="num">Registered</th><th>Registration</th>
                  </tr>
                </thead>
                <tbody>
                  {leagues.data?.map((l) => (
                    <tr key={l.id}>
                      <td><Link to={`/leagues/${l.id}`}>{l.name}</Link></td>
                      <td className="muted">{l.format.replace(/_/g, ' ')}</td>
                      <td className="num">{l.teamCount ?? 0}</td>
                      <td className="num">{l.registrationCount ?? 0}</td>
                      <td>
                        <Status value={l.registrationOpen ? 'open' : 'cancelled'} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
